import React from "react"
import { useParams } from "react-router-dom"
import { useShallow } from "zustand/react/shallow"
import { uniqBy } from "lodash"
import { AUTHORING_TASKS } from "constants/tasks.js"

// components
import Modal from "components/Modal/Modal"
import { Select, Checkbox, PrimeButton, ValidationMessage } from "components-design-system"
import { Tooltip, Input, Form } from "antd"
import DocsTable from "./components/DocsTable/DocsTable"
import VersionDetails from "./components/VersionDetails/VersionDetails"

// constants
import { SUBMISSION_REASONS, REGISTRY_OPTIONS } from "constants/submissions" // constants

// apis
import { useUserPermissions } from "api/hooks"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import { useGetEligibleCtgDocuments } from "api/hooks/studies/useDocumentsApi"
import { useGetStudy } from "api/hooks/studies/useStudiesApi"
import { useGetAuthoringVersions } from "api/hooks/studies/useAuthoringApi"
import PrsValidationsWithCount from "components/ag-grid-cell-renderers/PrsValidationsWithCount/PrsValidationsWithCount"
import { useGetGlobalSiteSettings } from "api/hooks/admin/usePrsSiteMaskingApi"
import { useGetAuthoringStudy } from "api/hooks/studies/useAuthoringApi"

// context
import useAppState from "context/hooks/useAppState"
import useSubmissions from "context/hooks/useSubmissions"
import useAuthoringValidationActions from "containers/studies/Study/subcomponents/Authoring/hooks/actions/useAuthoringValidationActions"
import useAuthoringValidationsStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringValidationsStore"

// styles
import "./UploadModal.scss"

const UploadModal = ({
  openModal,
  setOpenModal,
  onSubmitToCtg,
  onDownloadXml,
  onDownloadPdf,
  uploadLoading,
  approvalHistoryColDefs,
  approvals,
}) => {
  const { studyId } = useParams()
  const { data: study } = useGetStudy(studyId)
  const [form] = Form.useForm()
  const [formDisabled, setFormDisabled] = React.useState(false)
  const formTypesValue = Form.useWatch("form_type", form)
  const registryValue = Form.useWatch("registry", form)
  const isPrsCredentialChecked = Form.useWatch("prs-credentials", form)
  const prsOrganization = Form.useWatch("organization", form)
  const prsUsername = Form.useWatch("username", form)
  const prsPassword = Form.useWatch("password", form)
  const { formView, setFormView } = useAuthoringViewStore(
    useShallow((state) => ({
      formView: state.formView,
      setFormView: state.setFormView,
    }))
  )
  const { openToastNotification } = useAppState()
  const { taskType } = useAppState()

  const { formVersion, setFormVersion, setSelectedDocuments } = useSubmissions()
  const [modalTitle, setModalTitle] = React.useState("Create Submission")
  const [formVersionItems, setFormVersionItems] = React.useState([])
  const [showApprovalGrid, setShowApprovalGrid] = React.useState(true)
  const { data: permissions } = useUserPermissions()
  const { data: eligibleCtgDocuments = [], refetch: refetchEligibleCtgDocuments } = useGetEligibleCtgDocuments(
    study?.authoring_study_id
  )
  const { data: versions } = useGetAuthoringVersions(study?.authoring_study_id)
  const { data: globalSettings } = useGetGlobalSiteSettings()
  const { clearValidations, onRegistryValidate } = useAuthoringValidationActions(study?.authoring_study_id)
  const { data: authoring } = useGetAuthoringStudy(study?.authoring_study_id)
  const { validations } = useAuthoringValidationsStore(useShallow((state) => ({ validations: state.validations })))

  const prsRecordStatus = authoring?.prs_record_status?.toLowerCase()

  const getInitDocData = () => {
    let data = {}
    data.documents_list = []
    return data
  }

  const [documentsData, setDocumentsData] = React.useState(getInitDocData())

  const isPrsFormSubmission = REGISTRY_OPTIONS?.[registryValue]?.views.length > 1

  React.useEffect(() => {
    if (openModal) {
      refetchEligibleCtgDocuments()
      setFormView(null)
    }

    clearValidations()
  }, [openModal])

  React.useEffect(() => {
    const formVersionItems = uniqBy(versions, "version_num").map((versItem) => {
      const versNumber = Number(versItem.version_num).toFixed(1)
      return { id: versItem.version_num, text: versNumber }
    })

    setFormVersionItems(formVersionItems)
    if (formVersionItems?.[0]) setFormVersion(formVersionItems[0])
  }, [versions, setFormVersionItems])

  React.useEffect(() => {
    if (registryValue) {
      if (registryValue === "ctg") setModalTitle("Upload to PRS")
      else setModalTitle("Create Submission")
    }
  }, [registryValue])

  const showVersionDropdown = () => {
    if ((registryValue === "ctg" && !formTypesValue) || !registryValue) {
      return false
    } else {
      return true
    }
  }
  const doesHaveDocDate = () => {
    if (
      eligibleCtgDocuments?.length > 0 &&
      documentsData?.documents_list &&
      documentsData?.documents_list?.length > 0
    ) {
      const isEligible = documentsData.documents_list.find((doc) => doc.doc_date === null)
      return isEligible
    } else {
      return null
    }
  }

  const onCloseModal = () => {
    form.resetFields()
    setFormView(null)
    setOpenModal(false)
    setDocumentsData(getInitDocData())
  }

  const onReleaseToPrs = (data) => {
    const { organization, username, password } = data

    setFormDisabled(true)

    let payload = {
      study_db_id: study?.authoring_study_id,
      documents_list: documentsData?.documents_list
        ?.filter((doc) => {
          if (doc.prs_doc_id && doc.do_update === false && doc.do_delete === false && doc.do_add === false) return false
          return true
        })
        ?.map((doc) => {
          let updateDocObj = { ...doc }
          if (updateDocObj.doc_db_id === null) delete updateDocObj.doc_db_id
          if (updateDocObj.prs_doc_id === null) delete updateDocObj.prs_doc_id
          return updateDocObj
        }),
      form_types: formTypesValue ? convertFormTypes() : [],
      form_version: formVersion.id,
      submission_reason: data.submission_reason,
      autorelease: data.autorelease ? true : false,
      update_ver_date: data.update_ver_date ? true : false,
    }

    if (isPrsCredentialChecked) {
      payload.prs_creds = {
        prs_username: username,
        prs_password: password,
        prs_organization: organization,
      }
    }

    if (!formTypesValue) payload.form_version = null

    onSubmitToCtg(payload)

    // TODO: include the following inside an onSettled callback param sent to the onSubmitToCtg function
    onCloseModal()
    setFormDisabled(false)
  }

  const renderActions = React.useMemo(() => {
    const actions = [<PrimeButton onClick={onCloseModal}>Cancel</PrimeButton>]

    if (formView?.id === "ctg" || formView?.id === "eudra") {
      let isXmlDownloadDisabled = !formVersion
      if (formView.id === "ctg" && !formTypesValue) isXmlDownloadDisabled = true
      actions.push(
        <PrimeButton
          key={"download-xml-btn"}
          htmlType="button"
          type={formView?.id === "ctg" ? "default" : "primary"}
          disabled={isXmlDownloadDisabled || uploadLoading}
          onClick={() => {
            onDownloadXml(convertFormTypes())
            onCloseModal()
          }}
        >
          Download XML
        </PrimeButton>
      )
    } else if (formView?.id !== "ctg" && formView?.id !== "global") {
      actions.push(
        <PrimeButton
          key={"download-pdf-btn"}
          htmlType="button"
          type={formView?.id === "ctg" ? "default" : "primary"}
          disabled={!formView?.id || (REGISTRY_OPTIONS?.[formView?.id]?.views.length > 1 && !formTypesValue)}
          onClick={() => {
            onDownloadPdf(convertFormTypes())
            onCloseModal()
          }}
        >
          Download PDF
        </PrimeButton>
      )
    }
    if (formView?.id === "ctg" && permissions?.["study.form.submission"]) {
      actions.push(
        <PrimeButton
          key={"release-to-ctg"}
          type={"primary"}
          disabled={prsRecordStatus === "prs review"}
          tooltip={
            prsRecordStatus === "prs review"
              ? "This record has been Released (submitted) for PRS Review, and review has started and Study is locked. Submission cannot be done until PRS review is completed."
              : undefined
          }
          htmlType="button"
          onClick={() => {
            // manually validate DocsTable data and show custom error messages
            if (doesHaveDocDate()) {
              openToastNotification(
                "error",
                "Error",
                "File submitted to PRS must have a File Date. Please navigate to the Files tab to populate the File Date."
              )
            } else {
              form
                .validateFields()
                .then(() => {
                  // if no DocsTable validation errors, submit form
                  form.submit()
                })
                .catch((errorInfo) => {
                  console.log("errorInfo", errorInfo)
                  setFormDisabled(false)
                })
            }
          }}
        >
          <i className="fa-light fa-paper-plane" /> Submit to PRS
        </PrimeButton>
      )
    }

    return actions
  }, [formView?.id, formTypesValue, formVersion, prsRecordStatus])

  const onSetFormView = (option) => {
    setFormView({ id: option.value, text: option.label })
    clearValidations()
  }

  let width = "fit-content"
  if (registryValue === "ctg" || (showApprovalGrid && showVersionDropdown())) width = 820
  else if (registryValue === "jrct" || registryValue === "ctis") width = 440

  const showValidate = registryValue === "ctg" && formTypesValue && formVersion

  const isFormTypesRequired = () => {
    const required = registryValue === "ctg"
    return required ? false : true
  }
  const convertFormTypes = () => {
    for (let option of REGISTRY_OPTIONS?.[registryValue]?.views) {
      if (option.value === formTypesValue) {
        return option.formTypes
      }
    }
  }

  const onValuesChange = (changedValues) => {
    if (changedValues.form_type) {
      clearValidations()
    }
    if (changedValues.version) {
      let foundItem = formVersionItems.find((item) => {
        return item.id === changedValues.version
      })
      setFormVersion(foundItem)
      clearValidations()
    }
    if (changedValues.registryValue) {
      form.setFieldsValue({
        form_type: undefined,
      })
    }
  }

  return (
    <Modal
      title={modalTitle}
      open={openModal}
      onCancel={onCloseModal}
      hideImage={true}
      footer={[]}
      width={width}
      destroyOnClose={true}
      className={"upload-modal"}
    >
      <Form
        disabled={formDisabled || !formVersion}
        form={form}
        layout="vertical"
        onFinish={onReleaseToPrs}
        initialValues={{
          autorelease: globalSettings?.release_to_ctg ? true : false,
          update_ver_date: false,
          version: formVersion?.text,
          form_type: undefined,
        }}
        onValuesChange={onValuesChange}
      >
        <div className="submissions-actions-modal-content">
          <div
            className="submissions-form"
            layout={"vertical"}
          >
            <Form.Item
              label={"Registry"}
              className="study-form-action-bar-actions"
              colon={false}
              name={"registry"}
              rules={[{ required: true }]}
            >
              <Select
                style={{ minWidth: 150 }}
                onChange={(_, option) => onSetFormView(option)}
                allowClear={false}
                options={Object.keys(REGISTRY_OPTIONS).map((key) => REGISTRY_OPTIONS[key])}
                rules={[{ required: true }]}
              />
            </Form.Item>
            {(REGISTRY_OPTIONS?.[registryValue]?.views.length > 1 || !registryValue) && (
              <Tooltip
                open={registryValue === "ctg" ? undefined : false}
                title="Options available for combined or separate submissions of 'Protocol', 'Results', and other elements. Selection of a form is not mandatory for document submission."
              >
                <Form.Item
                  label={"Form"}
                  className="study-form-action-bar-actions"
                  name="form_type"
                  rules={[
                    {
                      required: isFormTypesRequired(),
                    },
                  ]}
                >
                  <Select
                    options={REGISTRY_OPTIONS?.[registryValue]?.views}
                    style={{ minWidth: 150 }}
                    allowClear={false}
                  />
                </Form.Item>
              </Tooltip>
            )}

            {showVersionDropdown() && (
              <Form.Item
                label={"Version"}
                className="study-form-action-bar-actions"
                colon={false}
                name={"version"}
              >
                <Select
                  options={formVersionItems}
                  allowClear={false}
                />
              </Form.Item>
            )}
            {showValidate && (
              <PrimeButton
                icon={<i className="fal fa-file-check" />}
                tooltip="Validate"
                loading={AUTHORING_TASKS.prsPreview === taskType}
                onClick={() => {
                  onRegistryValidate({
                    form_view: "ctg",
                    form_types: convertFormTypes(),
                    form_version: formVersion?.id,
                  })
                }}
                style={{ marginTop: 7 }}
              />
            )}
          </div>

          {validations && showValidate && (
            <div className="validations-container">
              <PrsValidationsWithCount
                value={[
                  ...validations.validations,
                  ...validations?.unmapped_validations?.map((validation) => ({
                    field_key: validation.source,
                    type: validation.severity,
                    text: validation.message,
                  })),
                ]}
                hasCount={false}
              />
            </div>
          )}

          <VersionDetails
            approvalHistoryColDefs={approvalHistoryColDefs}
            approvals={approvals}
            formVersion={formVersion}
            formTypes={formTypesValue}
            formView={registryValue}
            showVersion={() => showVersionDropdown()}
            showApprovalGrid={showApprovalGrid}
            setShowApprovalGrid={setShowApprovalGrid}
            selectedForms={() => convertFormTypes()}
          />
          {registryValue === "ctg" && (
            <div>
              <Form.Item
                name={"prs-credentials"}
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox defaultChecked={false}>Submit using individual credentials</Checkbox>
              </Form.Item>
              {isPrsCredentialChecked && (
                <div className="prs-upload-individual-credentials-wrapper">
                  <Form.Item
                    label="Organization"
                    name="organization"
                    rules={[{ required: true }]}
                  >
                    <Input autoComplete="off" />
                  </Form.Item>

                  <Form.Item
                    label="PRS username"
                    name="username"
                    rules={[{ required: true }]}
                  >
                    <Input autoComplete="off" />
                  </Form.Item>

                  <Form.Item
                    label="PRS password"
                    name="password"
                    rules={[{ required: true }]}
                  >
                    <Input.Password autoComplete="new-password" />
                  </Form.Item>
                </div>
              )}
              <DocsTable
                authoringId={study?.authoring_study_id}
                documentsData={documentsData}
                setDocumentsData={setDocumentsData}
                eligibleCtgDocuments={eligibleCtgDocuments}
                setSelectedDocuments={setSelectedDocuments}
                prsCredentials={
                  isPrsCredentialChecked
                    ? {
                        prs_username: prsUsername,
                        prs_password: prsPassword,
                        prs_organization: prsOrganization,
                      }
                    : null
                }
              />
              <div className="submissions-confirmation-container">
                {isPrsFormSubmission && formTypesValue && (
                  <div className="description-container">
                    Send all updates to PRS. If you would like to release record to CTG check the checkbox below.
                  </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", marginBottom: "10px" }}>
                  {isPrsFormSubmission && formTypesValue ? (
                    <>
                      <Form.Item
                        name={"autorelease"}
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                      >
                        <Checkbox>Release record</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name={"update_ver_date"}
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                      >
                        <Checkbox>Update record verification date</Checkbox>
                      </Form.Item>
                    </>
                  ) : (
                    <>
                      <Form.Item
                        name="autorelease"
                        value={false}
                        hidden
                      />
                      <Form.Item
                        name="update_ver_date"
                        value={false}
                        hidden
                      />
                    </>
                  )}
                  <Form.Item
                    rules={[{ required: true }]}
                    style={{ marginTop: "20px" }}
                    name="submission_reason"
                  >
                    <Select
                      options={SUBMISSION_REASONS?.filter((item) => {
                        if (isPrsFormSubmission && item === "Document Update") return false
                        return true
                      })}
                      placeholder={"Reason for submission"}
                      id={"submission_reason-field"}
                    />
                  </Form.Item>
                </div>
                <Form.Item
                  valuePropName="checked"
                  name={"agreeToSubmit"}
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Checkbox>I am in agreement to submit the finalized record to PRS</Checkbox>
                </Form.Item>
                {prsRecordStatus === "released" && (
                  <Form.Item
                    valuePropName="checked"
                    name={"agreeToSubmitPrsRecordStatusConfirmation"}
                    rules={[
                      {
                        required: true,
                      },
                    ]}
                  >
                    <Checkbox>
                      {prsRecordStatus === "released" &&
                        `This record has been Released (submitted) for PRS Review, but review has not yet started. Checking this box confirms you understand another submission will be made from Prime updating this record in PRS.`}
                    </Checkbox>
                  </Form.Item>
                )}
                {prsRecordStatus === "prs review" && (
                  <ValidationMessage
                    type="warning"
                    value={
                      "This record has been Released (submitted) for PRS Review, and review has started and Study is locked. Submission cannot be done until PRS review is completed."
                    }
                  />
                )}
              </div>
            </div>
          )}
          <div className="submission-modal-actions-container">{renderActions}</div>
        </div>
      </Form>
    </Modal>
  )
}

export default UploadModal
